/**
 * @file PromotionFormFields.tsx
 * @description Поля формы акции для PromotionEditDialog (phase18.3, promo.md P1-P3).
 *
 * Вынесено отдельно, чтобы уложиться в лимит ≤200 строк/файл. Контролируемая
 * форма: единый объект состояния + patch-style onChange (см. ProductBasicSection).
 */

import type { CatalogCategory, PromotionKind, PromotionScope } from "../../lib/api-catalog";
import { CHANNEL_LABELS, type SalesChannelKind } from "../../lib/api-channel-listings";
import { categoryTreeOptions } from "../../lib/catalog-category-tree";
import { Combobox } from "../ui/combobox";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { SelectField } from "../ui/SelectField";
import { Switch } from "../ui/switch";

/** Списки формы — данными (общий `SelectField` вместо нативного списка). */
const KIND_OPTIONS = [
  { value: "PERCENT", label: "Процент" },
  { value: "FIXED", label: "Сумма в рублях" },
];
const SCOPE_OPTIONS = [
  { value: "CATALOG", label: "Весь каталог" },
  { value: "CATEGORY", label: "Категория" },
  { value: "PRODUCT", label: "Товар" },
  { value: "SKU", label: "Фасовка" },
];
const CHANNEL_OPTIONS = [
  { value: "", label: "Все каналы" },
  ...(Object.keys(CHANNEL_LABELS) as SalesChannelKind[]).map((c) => ({ value: c, label: CHANNEL_LABELS[c] })),
];

export interface PromotionFormState {
  displayName: string;
  kind: PromotionKind;
  value: string;
  scopeType: PromotionScope;
  categoryId: string;
  productId: string;
  skuId: string;
  channel: string;
  startsAt: string;
  endsAt: string;
  isActive: boolean;
}

interface Props {
  form: PromotionFormState;
  categories: CatalogCategory[];
  disabled: boolean;
  onChange: (patch: Partial<PromotionFormState>) => void;
}

export function PromotionFormFields({ form, categories, disabled, onChange }: Props) {
  return (
    <>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="pr-name">Название *</Label>
        <Input
          id="pr-name"
          value={form.displayName}
          onChange={(e) => onChange({ displayName: e.target.value })}
          disabled={disabled}
          placeholder="Например: «Скидка на осенние сборы»"
          required
        />
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        <div className="flex-1 flex flex-col gap-1.5">
          <Label htmlFor="pr-kind">Тип скидки *</Label>
          <SelectField id="pr-kind" ariaLabel="Тип скидки" value={form.kind} onChange={(v) => onChange({ kind: v as PromotionKind })} options={KIND_OPTIONS} disabled={disabled} />
        </div>
        <div className="flex-1 flex flex-col gap-1.5">
          <Label htmlFor="pr-value">{form.kind === "PERCENT" ? "Размер, % (1–100)" : "Размер, ₽"} *</Label>
          <Input
            id="pr-value"
            type="number"
            min={form.kind === "PERCENT" ? 1 : 0.01}
            max={form.kind === "PERCENT" ? 100 : undefined}
            step={form.kind === "PERCENT" ? 1 : 0.01}
            value={form.value}
            onChange={(e) => onChange({ value: e.target.value })}
            disabled={disabled}
            required
          />
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="pr-scope">Область действия *</Label>
        <SelectField id="pr-scope" ariaLabel="Область действия" value={form.scopeType} onChange={(v) => onChange({ scopeType: v as PromotionScope })} options={SCOPE_OPTIONS} disabled={disabled} />
      </div>

      {form.scopeType === "CATEGORY" ? (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="pr-category">Категория *</Label>
          <Combobox
            id="pr-category"
            ariaLabel="Категория"
            options={categoryTreeOptions(categories.filter((c) => !c.isExternal))}
            value={form.categoryId || null}
            onChange={(v) => onChange({ categoryId: v ?? "" })}
            placeholder="Выберите категорию"
            searchPlaceholder="Найти категорию"
            disabled={disabled}
          />
        </div>
      ) : null}

      {form.scopeType === "PRODUCT" ? (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="pr-product">Id товара *</Label>
          <Input
            id="pr-product"
            value={form.productId}
            onChange={(e) => onChange({ productId: e.target.value })}
            disabled={disabled}
            placeholder="id из URL карточки товара"
          />
        </div>
      ) : null}

      {form.scopeType === "SKU" ? (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="pr-sku">Id фасовки *</Label>
          <Input
            id="pr-sku"
            value={form.skuId}
            onChange={(e) => onChange({ skuId: e.target.value })}
            disabled={disabled}
            placeholder="id фасовки товара"
          />
        </div>
      ) : null}

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="pr-channel">Канал</Label>
        <SelectField id="pr-channel" ariaLabel="Канал" value={form.channel} onChange={(v) => onChange({ channel: v })} options={CHANNEL_OPTIONS} disabled={disabled} />
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        <div className="flex-1 flex flex-col gap-1.5">
          <Label htmlFor="pr-starts">Начало *</Label>
          <Input
            id="pr-starts"
            type="datetime-local"
            value={form.startsAt}
            onChange={(e) => onChange({ startsAt: e.target.value })}
            disabled={disabled}
            required
          />
        </div>
        <div className="flex-1 flex flex-col gap-1.5">
          <Label htmlFor="pr-ends">Окончание</Label>
          <Input
            id="pr-ends"
            type="datetime-local"
            value={form.endsAt}
            onChange={(e) => onChange({ endsAt: e.target.value })}
            disabled={disabled}
          />
          <p className="type-caption text-on-surface-variant">Пусто — бессрочно</p>
        </div>
      </div>

      <label className="flex min-h-12 cursor-pointer items-center gap-3">
        <Switch checked={form.isActive} onCheckedChange={(v) => onChange({ isActive: v === true })} disabled={disabled} />
        <span className="type-body text-on-surface">Акция включена</span>
      </label>
    </>
  );
}
